/**
 * Wrong-engine schema-load guard.
 *
 * A connection id belongs to exactly one engine, but the paths that load a
 * schema for it pick their loader from an engine that can lag behind or fall
 * back: a query tab with no connection resolves to 'mssql', and the visual
 * query editor parses anything without a SQL dialect as 'postgres'. When the
 * two disagree, the loader sends its own dialect's catalog queries to another
 * engine's server — field telemetry 2026-09-24 showed T-SQL catalog queries on
 * MySQL-only machines and PostgreSQL ones on a Kusto-only machine — and the
 * failed load publishes an empty degraded snapshot on the schema bus, which is
 * keyed by (connection, database) only, over the real engine's snapshot.
 *
 * The rule (`isForeignEngine` in `connectionLookup.ts`) is enforced for every
 * engine at three places: here for the language-service provider registry
 * (each engine's `connectForLanguageServices`) and the schema-bus publish
 * (installed below when this module loads — the registry imports it, so it is
 * in place before any language-service schema load), and in the visual query
 * editor's live schema pull (`livePullSchemaSnapshot.ts`).
 */

import { getRecordedConnectionEngine, isForeignEngine } from '../utils/connectionLookup';
import { setPublishGuard } from '../../intellisense/schemaBus/schemaBus';

/** Where a skipped load was caught — the `site` property of the diagnostic event. */
export type ForeignEngineGuardSite = 'provider-connect' | 'schema-bus';

const reportedMismatches = new Set<string>();

/**
 * Should the load (or publish) for `connectionId` by a `loaderEngine` loader
 * be skipped? The first skip per (site, loader, connection engine) in a
 * session fires `feature:schema_load_engine_mismatch`, so the field shows
 * whether any path still reaches a wrong-engine load after the root fixes.
 */
export function shouldSkipForeignEngineLoad(
  connectionId: string,
  loaderEngine: string,
  site: ForeignEngineGuardSite,
): boolean {
  const connectionEngine = getRecordedConnectionEngine(connectionId);
  if (!connectionEngine || !isForeignEngine(connectionEngine, loaderEngine)) return false;
  reportMismatch(site, loaderEngine, connectionEngine);
  return true;
}

function reportMismatch(site: ForeignEngineGuardSite, loaderEngine: string, connectionEngine: string): void {
  const key = `${site}|${loaderEngine}|${connectionEngine}`;
  if (reportedMismatches.has(key)) return;
  reportedMismatches.add(key);
  if (typeof window === 'undefined') return;
  window.electronAPI?.analytics
    ?.track('feature:schema_load_engine_mismatch', { site, engine: loaderEngine, connectionEngine })
    .catch(() => {
      // Analytics must never affect UX.
    });
}

setPublishGuard(
  (snapshot) => !shouldSkipForeignEngineLoad(snapshot.connectionId, snapshot.engineId, 'schema-bus'),
);

/** Test seam — forget which mismatches were already reported this session. */
export function __resetConnectionEngineGuardForTest(): void {
  reportedMismatches.clear();
}
